import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  selectFeatureFlagPosCart,
  selectIsShadowModeEnabled,
} from '../../redux/features/config/config.selector';
import { selectUserProfile } from '../../selectors/user';
import {
  getCartSplit,
  getLayoutPreference,
  getLeftStackHeight,
  getRightStackHeight,
  removeLayoutPreference,
  removeStackHeights,
  resolvePaneUserKey,
  saveCartSplit,
  saveLayoutPreference,
  saveLeftStackHeight,
  saveRightStackHeight,
} from '../../utils/local-storage';
import LayoutPreferenceDialog, {
  CART_SPLIT_DEFAULT_SIZES,
  DEFAULT_PANE_SIZES,
  LEFT_STACK_DEFAULT_SIZES,
  LayoutPreferenceValues,
  RIGHT_STACK_DEFAULT_SIZES,
} from './LayoutPreferenceDialog';

/**
 * Hosts the "Layout preference" dialog for the whole app (App subtree), so the
 * header ProfileMenu can open it from anywhere — including the task-router lobby,
 * where the 3-pane console (and its PanelGroups) isn't mounted yet.
 *
 * Apply/Reset always persist the agent's explicit choice to local storage; when
 * the console IS mounted, MainPageLayout + the sidebars register a setter per
 * region so the same action also resizes the live panes/cards. Live sizes are
 * read through refs and snapshotted only when the dialog opens, so dragging
 * never re-renders this provider (which wraps the entire app).
 */

/**
 * HITL-1648 added `carts` — the My Cart | Live order column split inside the
 * right pane. It is a WIDTH split nested inside the right pane's top card,
 * which is why it is its own region rather than a third value on `right`: the
 * two resize independently, and one `Record` entry for both would make each
 * silently clobber the other.
 */
export type LayoutRegion = 'panes' | 'left' | 'right' | 'carts';
type PaneSetter = (sizes: number[]) => void;

interface LayoutPreferenceContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** Report the live sizes of a region on every layout change. */
  reportLiveSizes: (region: LayoutRegion, sizes: number[] | null) => void;
  /**
   * Register a region's imperative resize fn on mount and clear it (null) on
   * unmount, so Apply/Reset drive the live panes/cards only while they exist.
   */
  registerPaneSetter: (region: LayoutRegion, setter: PaneSetter | null) => void;
}

const LayoutPreferenceContext = createContext<LayoutPreferenceContextValue>({
  isOpen: false,
  open: () => {},
  close: () => {},
  reportLiveSizes: () => {},
  registerPaneSetter: () => {},
});

export const LayoutPreferenceProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const userProfile = useAppSelector(selectUserProfile);
  const userKey = resolvePaneUserKey(userProfile);
  const isPosCartEnabled = useAppSelector(selectFeatureFlagPosCart);
  // HITL-1648: the two-column split only exists when Supervised AI mode is on,
  // so its row is hidden otherwise rather than offering a control for a divider
  // that is not on screen.
  const isShadowModeEnabled = useAppSelector(selectIsShadowModeEnabled);

  const [isOpen, setIsOpen] = useState(false);
  // Values the dialog shows: snapshotted from the live panes/cards / saved
  // preference when it opens (see `open`), never on every drag.
  const [dialogValues, setDialogValues] = useState<{
    panes: number[];
    left: number[];
    right: number[];
    carts: number[];
  }>({
    panes: DEFAULT_PANE_SIZES,
    left: LEFT_STACK_DEFAULT_SIZES,
    right: RIGHT_STACK_DEFAULT_SIZES,
    carts: CART_SPLIT_DEFAULT_SIZES,
  });
  // Refs (not state) so per-frame drag reports don't re-render the whole app.
  const liveSizesRef = useRef<Record<LayoutRegion, number[] | null>>({
    panes: null,
    left: null,
    right: null,
    carts: null,
  });
  const setterRef = useRef<Record<LayoutRegion, PaneSetter | null>>({
    panes: null,
    left: null,
    right: null,
    carts: null,
  });

  const open = useCallback(() => {
    setDialogValues({
      panes:
        liveSizesRef.current.panes ??
        getLayoutPreference(userKey) ??
        DEFAULT_PANE_SIZES,
      left:
        liveSizesRef.current.left ??
        getLeftStackHeight(userKey) ??
        LEFT_STACK_DEFAULT_SIZES,
      right:
        liveSizesRef.current.right ??
        getRightStackHeight(userKey) ??
        RIGHT_STACK_DEFAULT_SIZES,
      // With Supervised AI mode off there is no second column to size, so this
      // falls through to the default and the dialog hides its row entirely.
      carts:
        liveSizesRef.current.carts ??
        getCartSplit(userKey) ??
        CART_SPLIT_DEFAULT_SIZES,
    });
    setIsOpen(true);
  }, [userKey]);

  const close = useCallback(() => setIsOpen(false), []);

  const reportLiveSizes = useCallback(
    (region: LayoutRegion, sizes: number[] | null) => {
      liveSizesRef.current[region] = sizes;
    },
    []
  );

  const registerPaneSetter = useCallback(
    (region: LayoutRegion, setter: PaneSetter | null) => {
      setterRef.current[region] = setter;
      // Region gone (e.g. agent returned to the lobby): forget its sizes so the
      // dialog falls back to the saved preference / default next time it opens.
      if (!setter) liveSizesRef.current[region] = null;
    },
    []
  );

  const handleApply = useCallback(
    (values: LayoutPreferenceValues) => {
      saveLayoutPreference(userKey, values.panes);
      saveLeftStackHeight(userKey, values.leftStack);
      saveRightStackHeight(userKey, values.rightStack);
      // Gated, unlike the three above: with supervised mode off the cart split
      // row is not rendered, so `values.cartSplit` is whatever the dialog fell
      // back to rather than anything the agent chose. Writing it would create a
      // key on a flag-off console — invisible, since nothing reads it there, but
      // this release's rule is that the flag off is byte-identical, and a new
      // localStorage entry is not nothing.
      //
      // `removeStackHeights` in handleReset deliberately stays UNgated: "Reset
      // all to default" should clear a split left behind from a session when the
      // flag was on.
      if (isShadowModeEnabled) saveCartSplit(userKey, values.cartSplit);
      setterRef.current.panes?.(values.panes);
      setterRef.current.left?.(values.leftStack);
      setterRef.current.right?.(values.rightStack);
      setterRef.current.carts?.(values.cartSplit);
    },
    [userKey, isShadowModeEnabled]
  );

  const handleReset = useCallback(() => {
    removeLayoutPreference(userKey);
    removeStackHeights(userKey);
    setterRef.current.panes?.(DEFAULT_PANE_SIZES);
    setterRef.current.left?.(LEFT_STACK_DEFAULT_SIZES);
    setterRef.current.right?.(RIGHT_STACK_DEFAULT_SIZES);
    setterRef.current.carts?.(CART_SPLIT_DEFAULT_SIZES);
  }, [userKey]);

  const value = useMemo(
    () => ({ isOpen, open, close, reportLiveSizes, registerPaneSetter }),
    [isOpen, open, close, reportLiveSizes, registerPaneSetter]
  );

  return (
    <LayoutPreferenceContext.Provider value={value}>
      {children}
      <LayoutPreferenceDialog
        open={isOpen}
        onClose={close}
        paneSizes={dialogValues.panes}
        leftStackSizes={dialogValues.left}
        rightStackSizes={dialogValues.right}
        cartSplitSizes={dialogValues.carts}
        isPosCartEnabled={isPosCartEnabled}
        isShadowModeEnabled={isShadowModeEnabled}
        onApply={handleApply}
        onReset={handleReset}
      />
    </LayoutPreferenceContext.Provider>
  );
};

export const useLayoutPreference = () => useContext(LayoutPreferenceContext);
